import React, { useState, useMemo } from "react";
import { Button, Card, Col, Form, Input, Row, Transfer, message, Modal } from "antd";
import { MinusCircleOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import i18n from "../../../../translations/i18n";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PRODUCTS_TABLE } from "../../../../constants/FirestoreConstant";
import _ from "lodash";
import { findCategoryById, findSubcategoryById } from "../../../../functions/categories";

const CustomTabsField = ({ products, currentProductId, currentProductCategories, form, categories }) => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [bulkEditModalVisible, setBulkEditModalVisible] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(null);
  const [targetKeys, setTargetKeys] = useState([]);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);

  // Helper function to get category names from IDs
  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || !categories) return "";
    return categoryIds
      .map((catId) => {
        // Try to find as main category first
        let category = findCategoryById(categories, catId);
        
        // If not found, try to find as subcategory
        if (!category) {
          category = findSubcategoryById(
            categories.flatMap((cat) => cat.subcategories || []),
            catId
          );
        }
        
        return category ? category.name : catId;
      })
      .join(", ");
  };

  // Get products in the same categories
  const relatedProducts = useMemo(() => {
    if (!products || !currentProductCategories || currentProductCategories.length === 0) {
      return [];
    }

    return products
      .filter((product) => {
        // Exclude current product
        if (product.id === currentProductId) return false;

        // Check if product has overlapping categories
        if (!product.categories || product.categories.length === 0) return false;

        return _.intersection(product.categories, currentProductCategories).length > 0;
      })
      .map((product) => ({
        key: product.id,
        title: product.name,
        description: getCategoryNames(product.categories),
        customTabs: product.customTabs || [],
      }));
  }, [products, currentProductCategories, currentProductId]);

  const openBulkEditModal = (tabIndex) => {
    setSelectedTabIndex(tabIndex);

    // Get the current tab being edited
    const customTabs = form.getFieldValue("customTabs");
    const selectedTab = customTabs[tabIndex];

    // Pre-select products that already have this tab
    if (selectedTab && selectedTab.title) {
      const preSelectedKeys = relatedProducts
        .filter((product) => {
          const existingTabs = product.customTabs || [];
          return existingTabs.some(
            (tab) => tab.title.toLowerCase() === selectedTab.title.toLowerCase()
          );
        })
        .map((product) => product.key);
      setTargetKeys(preSelectedKeys);
    } else {
      setTargetKeys([]);
    }

    setBulkEditModalVisible(true);
  };

  const getInitialTargetKeys = () => {
    const customTabs = form.getFieldValue("customTabs");
    const selectedTab = customTabs[selectedTabIndex];
    
    if (!selectedTab || !selectedTab.title) return [];
    
    return relatedProducts
      .filter((product) => {
        const existingTabs = product.customTabs || [];
        return existingTabs.some(
          (tab) => tab.title.toLowerCase() === selectedTab.title.toLowerCase()
        );
      })
      .map((product) => product.key);
  };

  const handleBulkEdit = async () => {
    const customTabs = form.getFieldValue("customTabs");
    const selectedTab = customTabs[selectedTabIndex];

    if (!selectedTab || !selectedTab.title || !selectedTab.content) {
      message.error(t("complete_tab_before_bulk_edit"));
      return;
    }

    setBulkEditLoading(true);

    try {
      const batch = firestore.batch();
      const initialKeys = getInitialTargetKeys();
      
      // Products to add tab to (newly selected)
      const productsToAdd = targetKeys.filter((key) => !initialKeys.includes(key));
      
      // Products to remove tab from (deselected)
      const productsToRemove = initialKeys.filter((key) => !targetKeys.includes(key));
      
      let addedCount = 0;
      let removedCount = 0;
      let skippedCount = 0;

      // Add tabs to newly selected products
      productsToAdd.forEach((productId) => {
        const product = relatedProducts.find((p) => p.key === productId);
        if (!product) return;

        // Check if product already has a tab with the same title
        const existingTabs = product.customTabs || [];
        const hasDuplicateTitle = existingTabs.some(
          (tab) => tab.title.toLowerCase() === selectedTab.title.toLowerCase()
        );

        if (hasDuplicateTitle) {
          skippedCount++;
          return;
        }

        // Add the tab to the product
        const updatedTabs = [...existingTabs, { title: selectedTab.title, content: selectedTab.content }];
        const productRef = firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(productId);
        batch.update(productRef, { customTabs: updatedTabs });
        addedCount++;
      });

      // Remove tabs from deselected products
      productsToRemove.forEach((productId) => {
        const product = relatedProducts.find((p) => p.key === productId);
        if (!product) return;

        const existingTabs = product.customTabs || [];
        // Remove the tab with matching title
        const updatedTabs = existingTabs.filter(
          (tab) => tab.title.toLowerCase() !== selectedTab.title.toLowerCase()
        );

        const productRef = firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(productId);
        batch.update(productRef, { customTabs: updatedTabs.length > 0 ? updatedTabs : null });
        removedCount++;
      });

      await batch.commit();

      let successMessage = "";
      if (addedCount > 0) {
        successMessage += t("bulk_edit_success", { count: addedCount });
      }
      if (removedCount > 0) {
        if (successMessage) successMessage += " ";
        successMessage += t("bulk_edit_removed", { count: removedCount });
      }
      if (skippedCount > 0) {
        if (successMessage) successMessage += " ";
        successMessage += t("bulk_edit_skipped", { count: skippedCount });
      }
      
      if (successMessage) {
        message.success(successMessage);
      } else {
        message.info(t("no_changes_made"));
      }

      setBulkEditModalVisible(false);
      setTargetKeys([]);
    } catch (error) {
      console.error("Bulk edit error:", error);
      message.error(t("bulk_edit_error"));
    } finally {
      setBulkEditLoading(false);
    }
  };

  const handleTransferChange = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys);
  };

  return (
    <Card title={t("custom_tabs")}>
      <Form.List name="customTabs">
        {(fields, { add, remove }) => {
          return (
            <div className="m-3">
              {fields.map((field, index) => (
                <Card
                  key={field.key}
                  size="small"
                  title={`${t("tab")} ${index + 1}`}
                  extra={
                    <div style={{ display: "flex", gap: "12px" }}>
                      <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => openBulkEditModal(index)}
                        disabled={relatedProducts.length === 0}
                      >
                        {t("apply_to_products")}
                      </Button>
                      <MinusCircleOutlined
                        onClick={() => remove(field.name)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        {...field}
                        label={t("tab_title")}
                        name={[field.name, "title"]}
                        rules={[
                          {
                            required: true,
                            message: i18n.t("form.enter_tab_title"),
                          },
                        ]}
                      >
                        <Input placeholder={t("form.enter_tab_title")} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        {...field}
                        label={t("tab_content")}
                        name={[field.name, "content"]}
                        rules={[
                          {
                            required: true,
                            message: i18n.t("form.enter_tab_content"),
                          },
                        ]}
                      >
                        <Input.TextArea
                          rows={6}
                          placeholder={t("form.enter_tab_content")}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  {t("add_custom_tab")}
                </Button>
              </Form.Item>
            </div>
          );
        }}
      </Form.List>

      <Modal
        title={t("apply_tab_to_products")}
        open={bulkEditModalVisible}
        onOk={handleBulkEdit}
        onCancel={() => setBulkEditModalVisible(false)}
        width={700}
        confirmLoading={bulkEditLoading}
        okText={t("apply")}
        cancelText={t("cancel")}
      >
        <p style={{ marginBottom: 16 }}>
          {t("bulk_edit_description")}
        </p>
        <Transfer
          dataSource={relatedProducts}
          titles={[t("available_products"), t("selected_products")]}
          targetKeys={targetKeys}
          onChange={handleTransferChange}
          render={(item) => (
            <div>
              <div style={{ fontWeight: 500 }}>{item.title}</div>
              <div style={{ fontSize: 12, color: "#888" }}>{item.description}</div>
            </div>
          )}
          listStyle={{
            width: 300,
            height: 400,
          }}
          showSearch
          filterOption={(inputValue, item) =>
            item.title.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
          }
        />
      </Modal>
    </Card>
  );
};

export default CustomTabsField;
